// biome-ignore-all lint/plugin/no-unlabeled-form-inputs: legacy unlabeled inputs, burn-down tracked in HTUI-392
import {
  BidirectionalArrowIcon,
  Box,
  Button,
  Column,
  Row,
  Select,
  StopIcon,
  Text,
  ChakraTooltip,
  tooltipVariants,
  TextWithTooltip,
} from "@hightouch/htui";
import groupBy from "lodash/groupBy";
import React, {
  type FC,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import type { GraphSeries } from "src/components/charts/engine/types";
import { formatMetricValue } from "src/components/charts/engine/utils";
import { ChartTooltip } from "src/components/charts/chart-tooltip";
import { isPresent } from "ts-extras";
import { generateHeatmapData } from "./heatmap-table-utils";
import { GraphScale } from "src/components/charts/engine/constants";
import { ChartVariant } from "src/pages/analytics/types";
import {
  ChartWithLoaderText,
  HeatMapChartLoader,
} from "src/components/charts/loaders";
import { LegendItemWrapper } from "src/components/analytics/shared/legend";

export type SortFn = (a: string, b: string) => number;

type MinimalHeatmapSeries = {
  metricName: string;
  audienceName?: string;
  splitName?: string;
  grouping?: { alias?: string; value: string; description?: string }[] | null;
  data: {
    metricValue: number;
    metadata?: { numerator?: number; denominator?: number };
    normalization?: string | null;
    aggregation?: any;
  }[];
};

// Types for custom rendering props
export type HeatmapCellData = {
  value: number;
  numerator?: number;
  denominator?: number;
  heatmapGrouping?: {
    type: string;
    dimension: string;
    value: string;
    description?: string;
  }[];
};

export type HeatmapRow = { dimension1: string; cells: HeatmapCellData[] };

export type SeriesContext = {
  metricName?: string;
  seriesName: string;
  colorScheme: string;
};

export type TooltipRenderer = (
  cell: HeatmapCellData,
  formattedValue: string,
  series: SeriesContext | undefined,
) => React.ReactNode;
export type CellColorFn = (cell: HeatmapCellData) => {
  bg: string;
  textColor: string;
} | null;
export type CellValueFormatter = (
  cell: HeatmapCellData,
  defaultFormattedValue: string,
) => string;
export type RowSortFn = (a: HeatmapRow, b: HeatmapRow) => number;

export type CellClickHandler = (
  cell: HeatmapCellData,
  row: HeatmapRow,
  columnHeader: string,
) => void;

export type RowClickHandler = (row: HeatmapRow) => void;

type HeatmapTableProps = {
  data: GraphSeries[] | MinimalHeatmapSeries[];
  scale?: GraphScale;
  isLoading?: boolean;
  showActionsAndLegend?: boolean;
  groupBySortFns?: [SortFn, SortFn];
  variant?: ChartVariant;
  compact?: boolean;
  isTruncated?: boolean;
  tooltipContainerRef?: React.RefObject<HTMLDivElement>;
  // Optional rendering overrides
  tooltipRenderer?: TooltipRenderer;
  cellColorFn?: CellColorFn;
  cellValueFormatter?: CellValueFormatter;
  rowSortFn?: RowSortFn;
  hideEmptyRowsAndColumns?: boolean;
  // Optional click handlers for drilldown
  onCellClick?: CellClickHandler;
  onRowClick?: RowClickHandler;
  // Custom rendering for row label cells (overrides the default text). Useful when
  // callers want to embed icons, links, or other rich content in row headers.
  rowLabelRenderer?: (row: HeatmapRow) => React.ReactNode;
  // Override the grid-template width for the row-label column. Defaults preserve
  // existing behavior keyed off `compact` and `isTruncated`.
  rowLabelWidth?: string;
  // Optional auxiliary column rendered between the row-label column and the
  // data cells. Use to surface per-row context (e.g. external IDs, tags) that
  // doesn't belong inside the row-label cell itself. The column is omitted
  // entirely when `auxColumnRenderer` is not provided.
  auxColumnHeader?: string;
  auxColumnRenderer?: (row: HeatmapRow) => React.ReactNode;
  auxColumnWidth?: string;
};

const COLOR_SCHEMES = ["indigo", "blue", "green"] as const;

export const HeatmapTable: FC<HeatmapTableProps> = ({
  data,
  scale = GraphScale.Linear,
  isLoading = false,
  showActionsAndLegend = true,
  groupBySortFns,
  variant = ChartVariant.Full,
  compact = false,
  isTruncated = true,
  tooltipContainerRef,
  tooltipRenderer,
  cellColorFn,
  cellValueFormatter,
  rowSortFn,
  hideEmptyRowsAndColumns = false,
  onCellClick,
  onRowClick,
  rowLabelRenderer,
  rowLabelWidth,
  auxColumnHeader,
  auxColumnRenderer,
  auxColumnWidth,
}) => {
  const [axesFlipped, setAxesFlipped] = useState(false);

  // We want to be able to show the following as dimensions on the heatmap:
  // - group by columns
  // - splits
  // - audiences
  const processedData = useMemo(() => {
    return data.map((series) => ({
      ...series,
      heatmapGrouping: [
        ...(series.grouping?.map((group) => ({
          type: "groupBy",
          dimension: group.alias ?? "",
          value: group.value ?? "",
          description: group.description ?? "",
        })) ?? []),
        series.splitName
          ? {
              dimension: "split",
              type: "split",
              value: series.splitName,
            }
          : undefined,
        series.audienceName
          ? {
              dimension: "audience",
              type: "audience",
              value: series.audienceName,
            }
          : undefined,
      ].filter(isPresent),
    }));
  }, [data]);

  // The heatmap can only support up to 2 dimensions, so we need to group the series by
  // the remaining dimensions and allow the user to select the series group they want to see
  const seriesGroups = useMemo(() => {
    const groups = groupBy(processedData, (series) => {
      const metricPart = series.metricName;
      const groupingPart = series.heatmapGrouping
        .slice(2)
        .map((group) => group.value)
        .join(" | ");

      return groupingPart ? `${metricPart} | ${groupingPart}` : metricPart;
    });

    return Object.entries(groups).map(([seriesName, series], index) => ({
      seriesName,
      series: series.map((s) => ({
        ...s,
        // We take the first two groupings as the actual groupings used in the heatmap
        heatmapGrouping: s.heatmapGrouping.slice(0, 2),
      })),
      metricName: series?.[0]?.metricName,
      audienceName: series?.[0]?.audienceName,
      splitName: series?.[0]?.splitName,
      colorScheme: COLOR_SCHEMES[index % COLOR_SCHEMES.length],
    }));
  }, [processedData]);

  // State for selected series
  const [selectedSeriesName, setSelectedSeriesName] = useState<string>(
    seriesGroups[0]?.seriesName ?? "",
  );

  const heatMapData = useMemo(() => {
    let data = generateHeatmapData(
      seriesGroups,
      selectedSeriesName,
      axesFlipped,
      groupBySortFns,
    );
    if (rowSortFn && data.grid.length > 0) {
      const sortedGrid = data.grid.toSorted(rowSortFn);
      data = {
        ...data,
        dimension1Values: sortedGrid.map((row) => row.dimension1),
        grid: sortedGrid,
      };
    }
    if (hideEmptyRowsAndColumns && data.grid.length > 0) {
      const isCellPresent = (cell: HeatmapCellData) =>
        cell.heatmapGrouping !== undefined;

      const nonEmptyColIndices = data.dimension2Values
        .map((_, colIdx) => colIdx)
        .filter((colIdx) =>
          data.grid.some((row) => {
            const cell = row.cells[colIdx];
            return cell && isCellPresent(cell);
          }),
        );

      const filteredGrid = data.grid
        .filter((row) => row.cells.some(isCellPresent))
        .map((row) => ({
          ...row,
          cells: nonEmptyColIndices.map((colIdx) => row.cells[colIdx]!),
        }));

      data = {
        dimension1Values: filteredGrid.map((row) => row.dimension1),
        dimension2Values: nonEmptyColIndices.map(
          (i) => data.dimension2Values[i]!,
        ),
        grid: filteredGrid,
        minValue: data.minValue,
        maxValue: data.maxValue,
      };
    }
    return data;
  }, [
    seriesGroups,
    selectedSeriesName,
    axesFlipped,
    groupBySortFns,
    rowSortFn,
    hideEmptyRowsAndColumns,
  ]);

  const getColorForValue = useCallback(
    (value: number, colorScheme: (typeof COLOR_SCHEMES)[number]) => {
      if (heatMapData.minValue === heatMapData.maxValue) {
        return `${colorScheme}.500`;
      }

      const normalizedValue =
        (value - heatMapData.minValue) /
        (heatMapData.maxValue - heatMapData.minValue);

      const colorIndex = Math.floor(normalizedValue * 8);
      return `${colorScheme}.${(colorIndex + 1) * 100}`;
    },
    [heatMapData.minValue, heatMapData.maxValue],
  );

  const getTextColorForValue = useCallback(
    (value: number) => {
      if (value === 0) return "text.secondary";

      if (heatMapData.minValue === heatMapData.maxValue) {
        return "white";
      }

      const normalizedValue =
        (value - heatMapData.minValue) /
        (heatMapData.maxValue - heatMapData.minValue);

      return normalizedValue >= 0.5 ? "white" : "text.primary";
    },
    [heatMapData.minValue, heatMapData.maxValue],
  );

  useEffect(() => {
    if (
      !selectedSeriesName ||
      !seriesGroups.find((group) => group.seriesName === selectedSeriesName)
    ) {
      setSelectedSeriesName(seriesGroups[0]?.seriesName ?? "");
    }
  }, [seriesGroups, selectedSeriesName]);

  const selectedSeries = seriesGroups.find(
    (group) => group.seriesName === selectedSeriesName,
  );

  const seriesContext: SeriesContext | undefined = selectedSeries
    ? {
        metricName: selectedSeries.metricName,
        seriesName: selectedSeries.seriesName,
        colorScheme: selectedSeries.colorScheme ?? COLOR_SCHEMES[0],
      }
    : undefined;

  const defaultTooltipRenderer = (
    cell: HeatmapCellData,
    formattedValue: string,
    series: SeriesContext | undefined,
  ) => {
    const subtitles = cell?.heatmapGrouping?.map(
      ({ dimension, value }) => `${dimension}: ${value || "--"}`,
    );
    if (
      cell.numerator !== undefined &&
      cell.denominator !== undefined &&
      subtitles
    ) {
      subtitles.push(
        `${cell.numerator} conversions / ${cell.denominator} sends`,
      );
    }
    return (
      <ChartTooltip
        wrapperVariant="minimal"
        title={series?.metricName ?? series?.seriesName ?? ""}
        subtitles={subtitles}
        color={`${series?.colorScheme}.500`}
        value={[{ value: formattedValue }]}
      />
    );
  };

  if (isLoading) {
    return (
      <ChartWithLoaderText justify="center" flex={1} minHeight={0} gap={8}>
        <HeatMapChartLoader />
      </ChartWithLoaderText>
    );
  }

  return (
    <Column flex={1} minHeight={0} gap={6}>
      {showActionsAndLegend && (
        <Row gap={2} justifyContent="space-between">
          {variant == ChartVariant.Full && (
            <Select
              size="sm"
              placeholder="Select a series…"
              value={selectedSeriesName}
              onChange={(v) => setSelectedSeriesName(v ?? "")}
              options={seriesGroups.map(({ seriesName }) => ({
                label: seriesName,
                value: seriesName,
              }))}
              optionAccessories={(option) => {
                const series = seriesGroups.find(
                  (group) => group.seriesName === option.value,
                );
                return {
                  leftAccessory: {
                    type: "icon",
                    icon: StopIcon,
                    color: `${series?.colorScheme}.500`,
                  },
                };
              }}
            />
          )}

          <Row gap={8}>
            {selectedSeries && (
              <LegendItemWrapper>
                <Text size="sm" variant="monospace" color="text.secondary">
                  Lowest value
                </Text>
                <Box
                  height={3}
                  width={24}
                  borderRadius="xl"
                  bgGradient={`linear(to right, ${[100, 200, 300, 400, 500, 600, 700, 800, 900].map((shade) => `${selectedSeries.colorScheme}.${shade}`).join(", ")})`}
                />
                <Text size="sm" variant="monospace" color="text.secondary">
                  Highest value
                </Text>
              </LegendItemWrapper>
            )}

            {variant == ChartVariant.Full && (
              <Button
                size="sm"
                variant="secondary"
                onClick={() => setAxesFlipped(!axesFlipped)}
                icon={BidirectionalArrowIcon}
              >
                Switch axes
              </Button>
            )}
          </Row>
        </Row>
      )}

      <Box flex={1} minHeight={0}>
        {!heatMapData.grid.length ? (
          <Column align="center" justify="center" height="100%">
            <Text color="text.tertiary">No data available</Text>
          </Column>
        ) : (
          <Box
            position="relative"
            height="100%"
            overflowX="auto"
            overflowY="auto"
          >
            <Box
              display="grid"
              gridTemplateColumns={(() => {
                const labelCol =
                  rowLabelWidth ??
                  (compact
                    ? "minmax(80px, 120px)"
                    : isTruncated
                      ? "max-content"
                      : "minmax(96px, 160px)");
                const auxCol = auxColumnRenderer
                  ? ` ${auxColumnWidth ?? "minmax(140px, 240px)"}`
                  : "";
                const dataCols = compact
                  ? `repeat(${heatMapData.dimension2Values.length}, minmax(80px, 1fr))`
                  : `repeat(${heatMapData.dimension2Values.length}, minmax(128px, 1fr))`;
                return `${labelCol}${auxCol} ${dataCols}`;
              })()}
              gap="2px"
              position="relative"
              bg="base.lightBackground"
              minWidth="fit-content"
            >
              {/* Empty cell in top-left corner */}
              <Box
                p={2}
                bg="white"
                position="sticky"
                left={0}
                top={0}
                zIndex={3}
              />

              {/* Aux column header */}
              {auxColumnRenderer && (
                <Box
                  py={2}
                  px={2}
                  position="sticky"
                  top={0}
                  bg="white"
                  zIndex={2}
                >
                  <Text color="text.secondary" size="sm" variant="monospace">
                    {auxColumnHeader ?? ""}
                  </Text>
                </Box>
              )}

              {/* Header row */}
              {heatMapData.dimension2Values.map((header, index) => (
                <Box
                  key={index}
                  py={2}
                  px={2}
                  textAlign="center"
                  position="sticky"
                  top={0}
                  bg="white"
                  zIndex={2}
                >
                  <TextWithTooltip
                    message={header || "--"}
                    color="text.secondary"
                    size="sm"
                    variant="monospace"
                  >
                    {header || "--"}
                  </TextWithTooltip>
                </Box>
              ))}

              {heatMapData.grid.map((row, rowIndex) => (
                <React.Fragment key={`row-${rowIndex}`}>
                  {/* Row header - make it sticky and optionally clickable */}
                  <Box
                    key={`header-${rowIndex}`}
                    py={2}
                    pr={2}
                    position="sticky"
                    left={0}
                    bg="white"
                    zIndex={2}
                    display="flex"
                    alignItems="center"
                    cursor={onRowClick ? "pointer" : undefined}
                    onClick={onRowClick ? () => onRowClick(row) : undefined}
                    borderRadius="md"
                    _hover={onRowClick ? { bg: "base.background" } : undefined}
                    transition="background-color 0.15s ease"
                  >
                    {rowLabelRenderer ? (
                      rowLabelRenderer(row)
                    ) : (
                      <TextWithTooltip
                        message={row.dimension1 || "--"}
                        color={onRowClick ? "text.primary" : "text.secondary"}
                        size="sm"
                        variant="monospace"
                        fontWeight={onRowClick ? "medium" : undefined}
                        isTruncated={isTruncated}
                      >
                        {row.dimension1 || "--"}
                      </TextWithTooltip>
                    )}
                  </Box>

                  {auxColumnRenderer && (
                    <Box
                      key={`aux-${rowIndex}`}
                      py={2}
                      px={2}
                      bg="white"
                      display="flex"
                      alignItems="center"
                      minWidth={0}
                    >
                      {auxColumnRenderer(row)}
                    </Box>
                  )}

                  {row.cells.map((cell, colIndex) => {
                    const defaultValue = formatMetricValue(
                      cell?.value ?? 0,
                      scale === GraphScale.Percent,
                    );
                    const formattedValue = cellValueFormatter
                      ? cellValueFormatter(cell, defaultValue)
                      : defaultValue;
                    const colors = cellColorFn?.(cell);
                    const bgColor =
                      colors?.bg ??
                      getColorForValue(
                        cell?.value ?? 0,
                        selectedSeries?.colorScheme ?? COLOR_SCHEMES[0],
                      );
                    const textColor =
                      colors?.textColor ??
                      getTextColorForValue(cell?.value ?? 0);

                    const tooltipContent = tooltipRenderer
                      ? tooltipRenderer(cell, formattedValue, seriesContext)
                      : defaultTooltipRenderer(
                          cell,
                          formattedValue,
                          seriesContext,
                        );

                    return (
                      <ChakraTooltip
                        key={`cell-${rowIndex}-${colIndex}`}
                        motionProps={tooltipVariants}
                        openDelay={150}
                        label={tooltipContent}
                        portalProps={
                          tooltipContainerRef
                            ? {
                                appendToParentPortal: false,
                                containerRef: tooltipContainerRef,
                              }
                            : undefined
                        }
                      >
                        <Box
                          borderRadius="lg"
                          p={compact ? 2 : 4}
                          textAlign="center"
                          bg={bgColor}
                          cursor={onCellClick ? "pointer" : undefined}
                          onClick={
                            onCellClick
                              ? () =>
                                  onCellClick(
                                    cell,
                                    row,
                                    heatMapData.dimension2Values[colIndex] ??
                                      "",
                                  )
                              : undefined
                          }
                          _hover={onCellClick ? { opacity: 0.8 } : undefined}
                        >
                          <Text
                            size="sm"
                            fontWeight="medium"
                            variant="monospace"
                            color={textColor}
                          >
                            {formattedValue}
                          </Text>
                        </Box>
                      </ChakraTooltip>
                    );
                  })}
                </React.Fragment>
              ))}
            </Box>
          </Box>
        )}
      </Box>
    </Column>
  );
};
